import { Badge } from "@/components/ui/badge";
import { useTranslation } from "react-i18next";

/** What the learning loop adds to a learning card: Radim's call, the reasons, the effect. */
export type LearningOutcomeFields = {
  status: string;
  reasonCodes?: string[];
  autoDecision?: "auto_accepted" | "auto_dismissed" | "needs_person" | null;
  autoNote?: string | null;
  effect?: { before: number; after: number; weeks: number } | null;
};

export function LearningOutcome({ learning }: { learning: LearningOutcomeFields }) {
  const { t } = useTranslation();
  const { autoDecision, autoNote, effect, reasonCodes } = learning;
  // A status set by the loop itself (retire, expire) says why in the note too.
  const loopStatus = learning.status === "retire_proposed" || learning.status === "expired";
  if (!autoDecision && !effect && !reasonCodes?.length && !(loopStatus && autoNote)) return null;
  return (
    <div className="space-y-1 text-xs text-muted-foreground" data-testid="learning-outcome">
      <div className="flex flex-wrap items-center gap-2">
        {autoDecision && (
          <Badge variant={autoDecision === "auto_dismissed" ? "outline" : "secondary"}>
            {t(`agentTabs.learningOutcome.decision.${autoDecision}`)}
          </Badge>
        )}
        {!!reasonCodes?.length && (
          <span>
            {t("agentTabs.learningOutcome.reasons")}:{" "}
            {reasonCodes.map((code) => (
              <code key={code} className="mr-1 rounded bg-muted px-1">
                {code}
              </code>
            ))}
          </span>
        )}
        {effect && (
          <span title={t("agentTabs.learningOutcome.effectHint")} data-testid="learning-effect">
            {t("agentTabs.learningOutcome.effect", { before: effect.before, after: effect.after })}{" "}
            ({t("agentTabs.learningOutcome.effectWeeks", { count: effect.weeks })})
          </span>
        )}
      </div>
      {autoNote && <p>{autoNote}</p>}
    </div>
  );
}

/** "Only auto-decided": the cards Radim or Hub decided without a person. */
export function AutoDecidedFilter({
  checked,
  onChange,
}: {
  checked: boolean;
  onChange: (checked: boolean) => void;
}) {
  const { t } = useTranslation();
  return (
    <label className="ml-auto flex items-center gap-1 self-center text-xs text-muted-foreground">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        data-testid="learnings-auto-only"
      />
      {t("agentTabs.learningOutcome.autoOnly")}
    </label>
  );
}
